import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router';
import {
    BarChartFill,
    CheckCircleFill,
    Heart,
    HeartFill,
    LightningFill,
    PlayFill,
} from '@goproblems/ui/icons';
import {
    useAddCollectionToFavoritesMutation,
    useCreateCollectionTestMutation,
    useRemoveCollectionFromFavoritesMutation,
} from '@api';
import {OverlayTrigger, Tooltip} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';

type CollectionTestActionsProps = {
    collectionId: number;
    numberOfProblems: number;
    isAuthenticated: boolean;
    hasPassedTest?: boolean | null;
    hasPassedTestFast?: boolean | null;
    initialFavorite?: boolean;
    showFavorite?: boolean;
    onFavoriteChanged?: (collectionId: number, isFavorite: boolean) => void;
};

type FavoriteToggleButtonProps = {
    collectionId: number;
    initialFavorite: boolean;
    isLoading: boolean;
    onToggleFavorite: (isFavorite: boolean) => Promise<void>;
};

type FavoriteToggleButtonViewProps = {
    collectionId: number;
    isFavorite: boolean;
    isLoading: boolean;
    onClick: () => void;
};

function FavoriteToggleButtonView({
    collectionId,
    isFavorite,
    isLoading,
    onClick,
}: FavoriteToggleButtonViewProps) {
    const {t} = useTranslation();
    const favoriteLabel = isFavorite
        ? t('problem_collection.remove_from_favorites')
        : t('problem_collection.add_to_favorites');

    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Tooltip id={`collection-favorite-tooltip-${collectionId}`}>
                    {favoriteLabel}
                </Tooltip>
            }>
            <span className="d-inline-block">
                <button
                    type="button"
                    className={`btn btn-sm ${isFavorite ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={onClick}
                    disabled={isLoading}
                    aria-pressed={isFavorite}
                    aria-label={favoriteLabel}>
                    {isFavorite ? <HeartFill /> : <Heart />}
                </button>
            </span>
        </OverlayTrigger>
    );
}

function FavoriteToggleButton({
    collectionId,
    initialFavorite,
    isLoading,
    onToggleFavorite,
}: FavoriteToggleButtonProps) {
    const [isFavorite, setIsFavorite] = useState(initialFavorite);

    useEffect(() => {
        setIsFavorite(initialFavorite);
    }, [initialFavorite]);

    const handleToggleFavorite = async () => {
        const previousFavorite = isFavorite;
        const nextFavorite = !isFavorite;

        setIsFavorite(nextFavorite);
        try {
            await onToggleFavorite(nextFavorite);
        } catch {
            setIsFavorite(previousFavorite);
        }
    };

    return (
        <FavoriteToggleButtonView
            collectionId={collectionId}
            isFavorite={isFavorite}
            isLoading={isLoading}
            onClick={() => {
                void handleToggleFavorite();
            }}
        />
    );
}

export const CollectionTestActions: React.FC<CollectionTestActionsProps> = ({
    collectionId,
    numberOfProblems,
    isAuthenticated,
    hasPassedTest,
    hasPassedTestFast,
    initialFavorite = false,
    showFavorite = false,
    onFavoriteChanged,
}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [createCollectionTest, {isLoading}] =
        useCreateCollectionTestMutation();
    const [addFavoriteCollection, {isLoading: isAddingFavorite}] =
        useAddCollectionToFavoritesMutation();
    const [removeFavoriteCollection, {isLoading: isRemovingFavorite}] =
        useRemoveCollectionFromFavoritesMutation();

    const hasTooFewProblems = numberOfProblems < 10;
    const disabled = !isAuthenticated || isLoading || hasTooFewProblems;
    const problemsNeededForTest = 10 - numberOfProblems;
    const tooFewProblemsTooltip = hasTooFewProblems
        ? t('problem_collection.test_requires_more_problems', {
              count: problemsNeededForTest,
          })
        : null;
    const isFavoriteLoading = isAddingFavorite || isRemovingFavorite;

    const onStartTest = async () => {
        const result = await createCollectionTest({collectionId}).unwrap();
        navigate(`/collection-tests/${result.id}`);
    };

    const handleToggleFavorite = async (isFavorite: boolean) => {
        try {
            if (isFavorite) {
                await addFavoriteCollection(collectionId).unwrap();
                onFavoriteChanged?.(collectionId, true);
            } else {
                await removeFavoriteCollection(collectionId).unwrap();
                onFavoriteChanged?.(collectionId, false);
            }
        } catch (error) {
            const message =
                (error as {data?: {message?: string}})?.data?.message ??
                t('problem_collection.failed_to_update_favorite');
            window.alert(message);
            console.error('Failed to update collection favorite:', error);
            throw error;
        }
    };

    const testButton = (
        <span className="d-inline-block">
            <button
                type="button"
                className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                onClick={onStartTest}
                disabled={disabled}
                aria-label={
                    tooFewProblemsTooltip ??
                    t('problem_collection.start_collection_test')
                }>
                <PlayFill />
                <span>{t('problem_collection.test')}</span>
            </button>
        </span>
    );

    return (
        <div className="d-flex align-items-center gap-2">
            {tooFewProblemsTooltip ? (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id={`collection-test-tooltip-${collectionId}`}>
                            {tooFewProblemsTooltip}
                        </Tooltip>
                    }>
                    {testButton}
                </OverlayTrigger>
            ) : (
                testButton
            )}
            <Link
                to={`/collection-tests/leaderboard/${collectionId}`}
                className="btn btn-outline-primary btn-sm d-flex align-items-center"
                title={t('problem_collection.collection_test_leaderboard')}
                aria-label={t(
                    'problem_collection.collection_test_leaderboard'
                )}>
                <BarChartFill />
            </Link>
            {hasPassedTestFast ? (
                <span
                    className="text-primary d-flex align-items-center"
                    title={t('problem_collection.all_10_solved_fast')}>
                    <LightningFill />
                </span>
            ) : hasPassedTest ? (
                <span
                    className="text-success d-flex align-items-center"
                    title={t('problem_collection.passed_test')}>
                    <CheckCircleFill />
                </span>
            ) : null}
            {showFavorite && isAuthenticated && (
                <FavoriteToggleButton
                    collectionId={collectionId}
                    initialFavorite={initialFavorite}
                    isLoading={isFavoriteLoading}
                    onToggleFavorite={handleToggleFavorite}
                />
            )}
        </div>
    );
};
